import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, ShieldAlert } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel, Chip } from "../components/shared";

const colorMap = { blue: "#3F4CE0", green: "#0E9355", red: "#E5484D", orange: "#E8590C" };

export default function UsaHub() {
  const { data: regulators } = useFetch("/regulators");
  const { data: extras } = useFetch("/regulatory-extras");
  const statePrivacyLaws = extras?.statePrivacyLaws || [];
  const breachPlaybook = extras?.breachPlaybook || [];
  return (
    <main data-testid="usa-hub-page">
      <section className="bg-[#F3EEE4] border-b border-[#E7E0D2]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-16">
          <SectionLabel color="#E8590C">USA Regulatory Hub</SectionLabel>
          <h1 className="font-display font-bold text-[38px] lg:text-[56px] tracking-tight leading-[1.03] max-w-4xl">
            Six regulators. Five reporting clocks. <span className="text-[#E8590C]">One hub.</span>
          </h1>
          <p className="mt-5 text-[16px] text-[#1A1914]/65 max-w-2xl">Control catalogues, applicability matrices, penalty frameworks and deadline calendars for SEC, Federal Banking Agencies, CISA, FinCEN, NIST and NYDFS — plus the state privacy-law patchwork.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Chip>SEC 4-business-day 8-K</Chip><Chip>Banking 36-hour</Chip><Chip>CIRCIA 72-hour</Chip><Chip>NYDFS 24-hour ransom</Chip>
          </div>
        </div>
      </section>

      <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="usa-regulator-grid">
          {(regulators || []).map((r) => (
            <Link key={r.slug} to={`/usa/${r.slug}`} data-testid={`regulator-card-${r.slug}`} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6 group flex flex-col">
              <div className="flex items-center justify-between">
                <span className="font-mono-label text-[11px] font-semibold" style={{ color: colorMap[r.color] }}>{r.abbr}</span>
                <span className="font-mono-label text-[9.5px] text-[#1A1914]/40">est. {r.established}</span>
              </div>
              <h3 className="mt-3 font-display font-semibold text-[20px] leading-snug group-hover:text-[#E8590C] transition-colors">{r.name}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#1A1914]/60 line-clamp-3">{r.summary}</p>
              <div className="mt-4 pt-4 border-t border-[#E7E0D2] flex items-center justify-between mt-auto">
                <span className="text-[12px] text-[#1A1914]/50">{r.domains} focus domains</span>
                <ArrowRight size={15} className="text-[#E8590C] group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section id="breach-playbook" className="bg-[#14130F] text-white">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
          <SectionLabel color="#12B76A">The multi-regulator breach playbook</SectionLabel>
          <h2 className="font-display font-bold text-[32px] lg:text-[44px] tracking-tight max-w-3xl">When one incident starts <span className="text-[#12B76A]">five clocks.</span></h2>
          <p className="mt-4 text-[15px] text-white/60 max-w-2xl">SEC · Banking agencies · CISA · NYDFS · State AGs — the same breach can trigger every one of them. Here's the hour-by-hour sequence.</p>
          <div className="mt-10 space-y-0" data-testid="breach-playbook-timeline">
            {breachPlaybook.map((step, i) => (
              <div key={step.hour} className="relative flex gap-5 pb-8 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className="w-20 shrink-0 font-mono-label text-[11px] text-[#12B76A] text-center py-1.5 border border-[#12B76A]/30 rounded-full bg-[#12B76A]/10">{step.hour}</span>
                  {i < breachPlaybook.length - 1 && <span className="flex-1 w-px bg-white/15 mt-2" />}
                </div>
                <div className="pb-2">
                  <h3 className="font-semibold text-[16.5px] flex items-center gap-2"><Clock3 size={15} className="text-white/40" /> {step.title}</h3>
                  <p className="mt-1.5 text-[14px] leading-relaxed text-white/60 max-w-2xl">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-20">
        <SectionLabel>The state patchwork</SectionLabel>
        <h2 className="font-display font-bold text-[30px] lg:text-[40px] tracking-tight">State privacy & security laws</h2>
        <p className="mt-3 text-[15px] text-[#1A1914]/60 max-w-2xl flex items-center gap-2"><ShieldAlert size={16} className="text-[#E8590C]" /> No federal privacy law yet — 20 states have filled the gap. The six that matter most:</p>
        <div className="mt-8 overflow-x-auto rounded-2xl border border-[#E7E0D2] bg-white">
          <table className="w-full text-left min-w-[760px]" data-testid="state-privacy-table">
            <thead>
              <tr className="border-b border-[#E7E0D2] bg-[#F3EEE4]">
                {["State", "Law", "Effective", "Scope trigger", "Enforcement", "Penalty"].map((h) => (
                  <th key={h} className="px-5 py-3.5 font-mono-label text-[10px] text-[#1A1914]/50">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {statePrivacyLaws.map((row) => (
                <tr key={row.state} className="border-b border-[#E7E0D2]/60 last:border-0 hover:bg-[#FAF7F1] transition-colors">
                  <td className="px-5 py-4 font-semibold text-[14px]">{row.state}</td>
                  <td className="px-5 py-4 text-[14px] text-[#3F4CE0] font-medium">{row.law}</td>
                  <td className="px-5 py-4 text-[13.5px] text-[#1A1914]/60">{row.effective}</td>
                  <td className="px-5 py-4 text-[13.5px] text-[#1A1914]/60">{row.scope}</td>
                  <td className="px-5 py-4 text-[13.5px] text-[#1A1914]/60">{row.enforcement}</td>
                  <td className="px-5 py-4 text-[13.5px] text-[#1A1914]/60">{row.penalty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
